import {ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Table} from "antd";
import {ColumnsType} from "antd/es/table";
import _ from "lodash";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, SbomProjectServiceContext} from "../../Contexts";
import Organization from "../../domain/Organization";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import {FindingSeverity, getEqualOrGreaterSeverities} from "../../domain/FindingSeverity";
import {Permission} from "../../domain/auth/Permission";
import {FormPersister} from "../../sal-ui/FormPersister";
import PagedResult from "../../service/PagedResult";
import {useTableHandler} from "../../sal-ui/TableHandler";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {formatDateTime} from "../../utils/FormatUtils";
import {DocumentTitle} from "../DocumentTitle";
import SbomProjectFilter, {createDefaultSbomProjectFilterValues} from "../sbomproject/SbomProjectFilter";
import {FocusedFindingAnalysisHost} from "../sbomproject/FocusedFindingAnalysisHost";
import {renderSbomProjectVersionAnalysisStatistics, SbomProjectVersionFindingStatisticsAction} from "../sbomproject/SbomProjectVersionStatistics";
import VulnerabilitiesLabel from "../sbomproject/VulnerabilitiesLabel";

import * as globalStyles from "../App.module.css";
import * as styles from "./DeviceList.module.css";

const FILTER_DEBOUNCE_MS = 500;
const DEVICE_VERSION_NAME = "os";
const PERSISTENT_IDENT = "DeviceList";
const RQB_FIELD_DEVICE_SEVERITY = "device.severity";
const RQB_FIELD_GROUP_ID = "groups.id";
const RQB_FIELD_NAME = "name";
const RQB_FIELD_ORGANIZATION_ID = "organization.id";
const RQB_FIELD_VERSION_NAME = "versions.name";

function DeviceList() {
    const appContext = useContext(AppContextContext);
    const title = "Devices";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <FocusedFindingAnalysisHost>
                    <DeviceTable/>
                </FocusedFindingAnalysisHost>
            </>
        </DocumentTitle>
    );
}

function DeviceTable() {
    const appContext = useContext(AppContextContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const navigate = useNavigate();
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        fixedRqbQuery: createDeviceRqbQuery(),
        persistentIdent: PERSISTENT_IDENT
    });
    const [sbomProjects, setSbomProjects] = useState<PagedResult<SbomProject>>();
    const [filterValues, setFilterValues] = useState<any>();
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        restoreFilterValues();
    }, []);

    useEffect(() => () => debouncedOnFinishFilter.cancel(), [debouncedOnFinishFilter]);

    const columns: ColumnsType<SbomProject> = [
        {
            dataIndex: "name",
            title: "Device",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: renderName
        },
        {
            dataIndex: "versions",
            title: "OS",
            render: (_, sbomProject) => renderDeviceVersion(sbomProject)
        },
        {
            dataIndex: "findingStatistics",
            title: <VulnerabilitiesLabel/>,
            render: (_, sbomProject) => <SbomProjectVersionFindingStatisticsAction
                projectVersion={getDeviceVersion(sbomProject)}
                sbomProject={sbomProject}
                onAfterFindingStatusSave={reload}
            />
        },
        {
            dataIndex: "analysisStatistics",
            title: "Findings analysis",
            width: 110,
            render: (_, sbomProject) => renderSbomProjectVersionAnalysisStatistics(getDeviceVersion(sbomProject))
        },
        {
            dataIndex: "sbomImportedAt",
            title: "Imported",
            width: 180,
            render: (_, sbomProject) => formatDateTime(getDeviceVersion(sbomProject)?.sbomImportedAt)
        }
    ];

    if (appContext.user.isSystemAdmin()) {
        columns.push({
            dataIndex: "organization",
            title: "Organization",
            render: renderOrganization
        });
    }

    const hasManageOrg = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG);

    return (
        <>
            <SbomProjectFilter
                className={styles.filter}
                values={filterValues}
                showGroupFilter={hasManageOrg}
                showOrganizationFilter={appContext.user.isSystemAdmin()}
                persistentStateIdent={PERSISTENT_IDENT}
                onChange={values => {
                    setFilterValues(values);

                    debouncedOnFinishFilter(values);
                }}
            />

            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
            </div>

            <Table className={styles.table}
                   showSorterTooltip={false}
                   loading={tableHandler.loading}
                   dataSource={sbomProjects?.data}
                   size="middle"
                   onChange={tableHandler.onTableChange}
                   pagination={tableHandler.pagination}
                   onRow={onRow}
                   rowKey="id"
                   columns={columns}
            />
        </>
    );

    function reload() {
        return sbomProjectService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setSbomProjects(value);
        });
    }

    function renderName(value: any, sbomProject: SbomProject) {
        const version = getDeviceVersion(sbomProject);

        return version
            ? <Link to={versionUrl(sbomProject, version)}>{value}</Link>
            : value;
    }

    function renderDeviceVersion(sbomProject: SbomProject) {
        const version = getDeviceVersion(sbomProject);

        if (!version) {
            return <i>missing OS version</i>;
        }

        return version.projectType || <i>unknown</i>;
    }

    function renderOrganization(value: Organization) {
        return <Link to={`/organizations/${value.id}`}>{value.name}</Link>;
    }

    function onFinishFilter(values: any) {
        filterFormPersister.saveState(values);

        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function restoreFilterValues() {
        const values = filterFormPersister.hasState()
            ? filterFormPersister.loadValues()
            : createDefaultSbomProjectFilterValues();

        setFilterValues(values);

        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        };

        values.name && rqbQuery.rules.push({
            field: RQB_FIELD_NAME,
            operator: "icontains",
            value: values.name
        });

        values.severity && values.severity.length > 0 && rqbQuery.rules.push({
            field: RQB_FIELD_DEVICE_SEVERITY,
            operator: "in",
            value: getEqualOrGreaterSeverities(values.severity as FindingSeverity)
        });

        if (values.groupIds?.length > 0) {
            rqbQuery.rules.push({
                field: RQB_FIELD_GROUP_ID,
                operator: "in",
                value: values.groupIds
            });
        }

        if (appContext.user.isSystemAdmin() && values.organizationIds?.length > 0) {
            rqbQuery.rules.push({
                field: RQB_FIELD_ORGANIZATION_ID,
                operator: "in",
                value: values.organizationIds
            });
        }

        return rqbQuery;
    }

    function onRow(sbomProject: SbomProject) {
        return {
            onDoubleClick: () => {
                const version = getDeviceVersion(sbomProject);

                if (version) {
                    navigate(versionUrl(sbomProject, version));
                }
            }
        };
    }
}

function createDeviceRqbQuery() {
    return {
        field: RQB_FIELD_VERSION_NAME,
        operator: "eq",
        value: DEVICE_VERSION_NAME
    };
}

function getDeviceVersion(sbomProject: SbomProject) {
    return sbomProject.versions?.find(version => version.name === DEVICE_VERSION_NAME);
}

function versionUrl(sbomProject: SbomProject, version: SbomProjectVersion) {
    return `/sbom-projects/${sbomProject.id}/${version.id}`;
}

export default DeviceList;
